import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import ServiceDeskAPIService from "@sd/services/APIs";
import { extractPayloadArray } from "@sd/lib/userDisplayMap";
import {
  SERVICE_DESK_FORMILY_ENDUSER_MODEL,
  serviceDeskFormilyEndUserGridQueryKeyWith,
} from "@sd/services/APIs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Search } from "lucide-react";
import { parseTicketGridTotal } from "@sd/lib/ticketStreamUtils";
import { TablePager } from "@sd/components/common/TablePager";
import { cn } from "@/lib/utils";

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}


function rowValue(
  row: Record<string, unknown>,
  fieldName: string
): unknown {
  const lower = fieldName.toLowerCase();
  if (lower in row) {
    return row[lower];
  }
  if (fieldName in row) {
    return row[fieldName];
  }
  const hit = Object.keys(row).find(k => k.toLowerCase() === lower);
  return hit ? row[hit] : undefined;
}

export function AdminGuestEndUserListing() {
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search → reset to page 1
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPageNum(1);
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const modelName = SERVICE_DESK_FORMILY_ENDUSER_MODEL;

  const searchParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (debouncedSearch) p.fullname = debouncedSearch;
    return p;
  }, [debouncedSearch]);

  const {
    data: listPayload,
    isPending,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [...serviceDeskFormilyEndUserGridQueryKeyWith(pageNum, pageSize), debouncedSearch],
    queryFn: async () => {
      const data = await ServiceDeskAPIService.formily.getModelListPage(
        modelName,
        pageNum,
        pageSize,
        searchParams
      );
      if (data === undefined) {
        throw new Error("Failed to load end users");
      }
      return data;
    },
    staleTime: 30_000,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const { data: columnGridData, isLoading: columnsLoading } = useQuery({
    queryKey: ["columnGridData", modelName],
    queryFn: () => ServiceDeskAPIService.schema.getColumnGridData(modelName),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const rows = useMemo(() => {
    return extractPayloadArray(listPayload ?? null).filter(
      (r): r is Record<string, unknown> =>
        r !== null && typeof r === "object" && !Array.isArray(r)
    );
  }, [listPayload]);

  const filteredRows = rows;

  const columns = useMemo(() => {
    const grid = columnGridData?.grid;
    if (Array.isArray(grid) && grid.length > 0) {
      return grid.map((field: { field_name: string; field_title: string }) => ({
        key: String(field.field_name).toLowerCase(),
        title: field.field_title ?? field.field_name,
        fieldName: String(field.field_name),
      }));
    }
    if (rows.length > 0) {
      return Object.keys(rows[0])
        .filter(k => !k.startsWith("__"))
        .sort()
        .map(k => ({
          key: k.toLowerCase(),
          title: k.replace(/_/g, " "),
          fieldName: k,
        }));
    }
    return [];
  }, [columnGridData, rows]);

  const totalCount = parseTicketGridTotal(listPayload);
  const backendTotalPages =
    totalCount !== undefined
      ? Math.max(1, Math.ceil(totalCount / pageSize))
      : 1;

  return (
    <div className="flex min-w-0 flex-col gap-4 p-4 sm:p-6">
      <Card className="border-border shadow-sm">
        <CardHeader className="space-y-0 px-4 pb-4 pt-6 sm:px-6">
          <CardTitle className="text-xl font-semibold tracking-tight">
            Guest (End users)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-6 sm:px-6">
          <div className="relative mb-4 w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              autoComplete="off"
              placeholder="Search…"
              className={cn(
                "h-10 w-full pl-9",
                searchInput ? "pr-[4.5rem]" : "pr-3"
              )}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              aria-label="Search end users"
            />
            {searchInput ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 h-8 -translate-y-1/2 px-2 text-xs text-muted-foreground"
                onClick={() => setSearchInput("")}
              >
                Clear
              </Button>
            ) : null}
          </div>
        
          {isError && (
            <p className="text-destructive mb-4 text-sm">
              {(error as Error)?.message ?? "Failed to load end users."}
            </p>
          )}

          <div className="overflow-x-auto rounded-md border">
            <Table className="min-w-max">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50 border-b border-border">
                  {columns.map(col => (
                    <TableHead
                      key={col.key}
                      className="whitespace-nowrap text-xs font-bold uppercase tracking-wide text-foreground"
                    >
                      {col.title}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(isPending || columnsLoading) && (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(columns.length, 1)}
                      className="text-muted-foreground h-24 text-center"
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </span>
                    </TableCell>
                  </TableRow>
                )}
                {!isPending &&
                  !columnsLoading &&
                  rows.length === 0 &&
                  columns.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-muted-foreground h-24 text-center"
                      >
                        No records on this page.
                      </TableCell>
                    </TableRow>
                  )}
                {!isPending &&
                  !columnsLoading &&
                  columns.length === 0 && (
                    <TableRow>
                      <TableCell className="text-muted-foreground h-24 text-center">
                        No columns loaded. Check schema / API access.
                      </TableCell>
                    </TableRow>
                  )}
                {!isPending &&
                  !columnsLoading &&
                  filteredRows.map((row, i) => (
                    <TableRow key={String(row.id ?? row.pk ?? i)}>
                      {columns.map(col => (
                        <TableCell
                          key={col.key}
                          className={cn("max-w-[280px] truncate text-sm")}
                          title={formatCell(rowValue(row, col.fieldName))}
                        >
                          {formatCell(rowValue(row, col.fieldName))}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {!isPending && !isError && (
            <div className="overflow-x-auto">
              <TablePager
                pageNum={pageNum}
                totalPages={backendTotalPages}
                pageSize={pageSize}
                isLoading={isFetching}
                onPageChange={setPageNum}
                onPageSizeChange={size => { setPageSize(size); setPageNum(1); }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
